import { useEffect, useRef, useCallback } from 'react';
import { Box } from '@chakra-ui/react';

interface Star {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  color: string;
  type: 'normal' | 'galaxy';
}

const StarField = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number>();
  const starsRef = useRef<Star[]>([]);

  const getRandomColor = () => {
    const colors = [
      'rgba(70, 255, 255, opacity)', // Cyan
      'rgba(255, 150, 220, opacity)', // Pink
      'rgba(150, 200, 255, opacity)', // Light Blue
      'rgba(255, 255, 255, opacity)', // White
      'rgba(255, 200, 100, opacity)', // Warm Yellow
    ];
    return colors[Math.floor(Math.random() * colors.length)];
  };

  const createStar = useCallback((): Star => {
    const isGalaxy = Math.random() < 0.1; // 10% chance of being a galaxy cluster
    const isMobile = window.innerWidth < 768;
    return {
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * (isGalaxy ? 0.05 : 0.2) * (isMobile ? 0.5 : 1), // Slower on mobile
      vy: (Math.random() - 0.5) * (isGalaxy ? 0.05 : 0.2) * (isMobile ? 0.5 : 1),
      size: isGalaxy
        ? Math.random() * (isMobile ? 2 : 3) + (isMobile ? 1 : 2)
        : Math.random() * (isMobile ? 1 : 1.5) + (isMobile ? 0.3 : 0.5),
      opacity: isGalaxy ? Math.random() * 0.15 + 0.1 : Math.random() * 0.25 + 0.2,
      color: getRandomColor(),
      type: isGalaxy ? 'galaxy' : 'normal',
    };
  }, []);

  const initStars = useCallback(() => {
    const isMobile = window.innerWidth < 768;
    const starCount = isMobile ? 50 : 100; // Fewer stars on mobile
    starsRef.current = Array(starCount).fill(null).map(createStar);
  }, [createStar]);

  const drawStars = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Deep space background gradient
    const gradient = ctx.createRadialGradient(
      canvas.width / 2,
      canvas.height / 2,
      0,
      canvas.width / 2,
      canvas.height / 2,
      canvas.width / 2
    );
    gradient.addColorStop(0, 'rgba(11, 61, 145, 0.1)');
    gradient.addColorStop(0.5, 'rgba(20, 20, 50, 0.07)');
    gradient.addColorStop(1, 'rgba(2, 8, 20, 0.05)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const isMobile = window.innerWidth < 768;
    const glowSize = isMobile ? 2 : 4; // Smaller glow on mobile

    starsRef.current.forEach((star) => {
      star.x += star.vx;
      star.y += star.vy;

      // Wrap around screen
      if (star.x < 0) star.x = canvas.width;
      if (star.x > canvas.width) star.x = 0;
      if (star.y < 0) star.y = canvas.height;
      if (star.y > canvas.height) star.y = 0;

      // Draw star
      ctx.beginPath();
      ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
      ctx.fillStyle = star.color.replace('opacity', star.opacity.toString());
      ctx.fill();

      // Glow effect
      if (star.type === 'galaxy') {
        // Larger, more diffuse glow for galaxy clusters
        const galaxyGlow = ctx.createRadialGradient(
          star.x,
          star.y,
          0,
          star.x,
          star.y,
          star.size * glowSize
        );
        galaxyGlow.addColorStop(0, star.color.replace('opacity', (star.opacity * 0.3).toString()));
        galaxyGlow.addColorStop(
          0.5,
          star.color.replace('opacity', (star.opacity * 0.1).toString())
        );
        galaxyGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = galaxyGlow;
        ctx.fill();
      } else {
        // Subtle glow for normal stars
        const starGlow = ctx.createRadialGradient(
          star.x,
          star.y,
          0,
          star.x,
          star.y,
          star.size * glowSize
        );
        starGlow.addColorStop(0, star.color.replace('opacity', (star.opacity * 0.3).toString()));
        starGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.fillStyle = starGlow;
        ctx.fill();
      }
    });

    animationFrameRef.current = requestAnimationFrame(drawStars);
  }, []);

  const handleResize = useCallback(() => {
    if (canvasRef.current) {
      canvasRef.current.width = window.innerWidth;
      canvasRef.current.height = window.innerHeight;
      initStars();
    }
  }, [initStars]);

  useEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    animationFrameRef.current = requestAnimationFrame(drawStars);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [drawStars, handleResize]);

  return (
    <Box
      as='canvas'
      ref={canvasRef}
      position='fixed'
      top='0'
      left='0'
      width='100%'
      height='100%'
      zIndex='0'
      pointerEvents='none'
      aria-hidden='true'
      sx={{
        mixBlendMode: 'screen',
      }}
    />
  );
};

export default StarField;
